import {HttpClient} from '@angular/common/http';
import {computed, inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DateRange} from '@domain/agenda';
import {Agenda} from '@domain/clinic/agenda';
import {Status} from '@domain/response-status';
import {TranslateService} from '@ngx-translate/core';
import {SnackBarService} from '@shared/snackbar/snackbar.service';
import {catchError, EMPTY, map, merge, Subject, switchMap} from 'rxjs';
import {RequestStatus} from 'src/infrastructure/request-status.enum';

@Injectable({
    providedIn: 'root'
})
export class AgendaService
{
    private readonly httpClient = inject(HttpClient);

    public readonly snackBar = inject(SnackBarService);

    public readonly translate = inject(TranslateService);

    private readonly state = signal<Agenda & Status & {isSuspended: boolean}>({
        status: RequestStatus.NOT_EXECUTED,
        appointments: [],
        unavailableSlots: [],
        workingDays: [],
        isSuspended: false
    });

    public readonly loading = computed(() => this.state().status === RequestStatus.PENDING);

    public readonly fetched = computed(() => this.state().status === RequestStatus.CONCLUDED);

    public readonly appointments = computed(() => this.state().appointments);

    public readonly unavailableSlots = computed(() => this.state().unavailableSlots);

    public readonly workingDays = computed(() => this.state().workingDays);

    public readonly isSuspended = computed(() => this.state().isSuspended);

    private readonly getSuspendedStatus$ = new Subject<void>();

    private readonly suspendedStatusFetched$ = this.getSuspendedStatus$.pipe(
        switchMap(() =>
        {
            return this.httpClient.get<{isSuspended: boolean}>('agenda/check-suspension').pipe(
                map(({isSuspended}) => isSuspended)
            );
        })
    );

    private readonly suspendAgenda$ = new Subject<DateRange>();

    private readonly agendaSuspended$ = this.suspendAgenda$.pipe(
        switchMap((data) =>
        {
            return this.httpClient.patch('agenda/change-agenda-status', data).pipe(
                catchError(() =>
                {
                    this.snackBar.showErrorSnackbar(data ?
                        this.translate.instant('GENERAL.RESPONSE_ERRORS.AGENDA_SUSPEND_NOT_COMPLETE') :
                        this.translate.instant('GENERAL.RESPONSE_ERRORS.AGENDA_UNSUSPEND_NOT_COMPLETE')
                    );

                    this.state.update((previousState) =>
                        ({
                            ...previousState,
                            status: RequestStatus.ERROR
                        }));

                    return EMPTY;
                }),
                map(() =>
                {
                    this.snackBar.showSuccessSnackbar(data ?
                        this.translate.instant('CLINICIAN.AGENDA.AGENDA_SUSPENDED') :
                        this.translate.instant('CLINICIAN.AGENDA.AGENDA_UNSUSPENDED')
                    );
                })
            );
        })
    );

    private readonly setUnavailableSlot$ = new Subject<DateRange>();

    private readonly unavailableSlotSetted$ = this.setUnavailableSlot$.pipe(
        switchMap(({startDate, endDate}) =>
        {
            return this.httpClient.post('unavailable-slot', {startDate, endDate}).pipe(
                catchError(() =>
                {
                    this.snackBar.showErrorSnackbar(this.translate.instant('GENERAL.RESPONSE_ERRORS.UNAVAILABILITY_NOT_COMPLETE'));

                    this.state.update((previousState) =>
                        ({
                            ...previousState,
                            status: RequestStatus.ERROR
                        }));

                    return EMPTY;
                })
            );
        })
    );

    private readonly removeUnavailableSlot$ = new Subject<string>();

    private readonly unavailableSlotRemoved$ = this.removeUnavailableSlot$.pipe(
        switchMap((unavailableSlotId) =>
        {
            return this.httpClient.delete(`unavailable-slot/${unavailableSlotId}`).pipe(
                catchError(() =>
                {
                    this.snackBar.showErrorSnackbar(this.translate.instant('GENERAL.RESPONSE_ERRORS.UNAVAILABLE_SLOT_NOT_REMOVED'));
                    
                    this.state.update((previousState) =>
                        ({
                            ...previousState,
                            status: RequestStatus.ERROR
                        }));

                    return EMPTY;
                })
            );
        })
    );

    private readonly getAgenda$ = new Subject<void>();

    private readonly agendaFetched$ = this.getAgenda$.pipe(
        switchMap(() =>
        {
            return this.httpClient.get<Agenda>('agenda').pipe(
                catchError(() =>
                {
                    this.snackBar.showErrorSnackbar(this.translate.instant('GENERAL.RESPONSE_ERRORS.AGENDA_NOT_FOUND'));

                    this.state.update((previousState) =>
                        ({
                            ...previousState,
                            status: RequestStatus.ERROR
                        }));

                    return EMPTY;
                }),
                map(Agenda.fromJSON)
            );
        })
    );

    private readonly cancelAppointment$ = new Subject<string>();

    private readonly appointmentCancelled$ = this.cancelAppointment$.pipe(
        switchMap((appointmentId) =>
        {
            return this.httpClient.patch(`clinician/appointments/${appointmentId}/cancel`, null).pipe(
                catchError(() =>
                {
                    this.snackBar.showErrorSnackbar(this.translate.instant('GENERAL.RESPONSE_ERRORS.APPOINTMENT_CANCEL_NOT_COMPLETE'));

                    this.state.update((previousState) =>
                        ({
                            ...previousState,
                            status: RequestStatus.ERROR
                        }));

                    return EMPTY;
                }),
                map(() =>
                {
                    this.snackBar.showSuccessSnackbar(this.translate.instant('CLINICIAN.AGENDA.APPOINTMENT_CANCEL_COMPLETE'));
                })
            );
        })
    );

    public constructor()
    {
        this.agendaFetched$.pipe(takeUntilDestroyed()).subscribe({
            next: ({appointments, unavailableSlots, workingDays}) =>
            {
                this.state.update((previousState) =>
                    ({
                        ...previousState,
                        status: RequestStatus.CONCLUDED,
                        appointments,
                        unavailableSlots,
                        workingDays
                    }));

                this.getSuspendedStatus();
            }
        });

        this.suspendedStatusFetched$.pipe(takeUntilDestroyed()).subscribe({
            next: (isSuspended) =>
            {
                this.state.update((previousState) =>
                    ({
                        ...previousState,
                        isSuspended
                    })
                );
            }
        });

        merge(this.appointmentCancelled$, this.unavailableSlotSetted$, this.agendaSuspended$, this.unavailableSlotRemoved$)
            .pipe(takeUntilDestroyed()).subscribe({
                next: () =>
                {
                    this.state.update((previousState) =>
                        ({
                            ...previousState,
                            status: RequestStatus.CONCLUDED
                        }));

                    this.getAgenda();
                }
            });
    }

    public getAgenda(): void
    {
        this.state.update((previousState) =>
            ({
                ...previousState,
                status: RequestStatus.PENDING
            }));

        this.getAgenda$.next();
    }

    public setUnavailableSlot({startDate, endDate}: DateRange): void
    {
        this.setUnavailableSlot$.next({startDate, endDate});
    }

    public cancelAppointment(appointmentId: string): void
    {
        this.cancelAppointment$.next(appointmentId);
    }

    public removeUnavailableSlot(unavailableSlotId: string): void
    {
        this.removeUnavailableSlot$.next(unavailableSlotId);
    }

    public suspendAgenda(data: DateRange | null): void
    {
        this.suspendAgenda$.next(data);
    }

    public getSuspendedStatus(): void
    {
        this.getSuspendedStatus$.next();
    }

    public clearState(): void
    {
        this.state.set({
            status: RequestStatus.NOT_EXECUTED,
            appointments: [],
            unavailableSlots: [],
            workingDays: [],
            isSuspended: false
        });
    }
}
